import React, { useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { DeleteErrorDialog as DeleteErrorDialogType } from '@/contexts/ToastContext';

interface DeleteErrorDialogProps {
  dialog: DeleteErrorDialogType;
  onClose: () => void;
}

export const DeleteErrorDialog: React.FC<DeleteErrorDialogProps> = ({ dialog, onClose }) => {
  console.log('🚫 DeleteErrorDialog renderizado:', dialog);

  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getIcon = () => {
    switch (dialog.type) {
      case 'danger':
        return <AlertTriangle className="h-6 w-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
    }
  };

  const handleConfirm = () => {
    dialog.onConfirm();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop con efecto focal */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal centrado */}
      <div className="relative bg-brand-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getIcon()}
            <h3 className="text-lg font-semibold text-brand-gray-dark sentence-case">
              {dialog.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-brand-gray-dark sentence-case leading-relaxed mb-4">
            {dialog.message}
          </p>

          {/* Error reason section with red highlighting */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold text-center">{dialog.errorReason}</p>
          </div>
        </div>

        {/* Footer - Only one button (Done) */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-brand-orange text-white rounded-md hover:bg-brand-orange-dark transition-colors duration-200 sentence-case font-medium"
          >
            {dialog.confirmText || 'Listo'}
          </button>
        </div>
      </div>
    </div>
  );
};
